import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, Lock, Upload } from 'lucide-react';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { GuidedTour } from '../components/onboarding/guided-tour.js';
import { SampleDataCard } from '../components/onboarding/sample-data-card.js';
import { SetupChecklist } from '../components/onboarding/setup-checklist.js';
import { Button, Card, CardContent, CardHeader, CardTitle, ListSkeleton } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useOnboarding } from '../lib/onboarding/use-onboarding.js';

/**
 * Setting the shop up — a screen an owner can leave at any point and come back to.
 *
 * There is no "next" button and no step counter marching forward, because this is not a form
 * that has to be finished. A shop that is half-configured must be able to book an order, and
 * the owner WILL be interrupted by a customer in the middle of this; a wizard that has to be
 * completed before the product works is a wizard abandoned along with the trial.
 *
 * The import lives here too. Every shop already has its customer list somewhere, and the
 * fifteen minutes after signup is exactly when they are willing to go and find it.
 */
export function SetupPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const isOwner = role === 'owner';

  const onboarding = useOnboarding();

  if (!isOwner) {
    return (
      <>
        <PageHeader title={t('onboarding.title')} />
        <EmptyState
          icon={Lock}
          title={t('onboarding.ownerOnlyTitle')}
          description={t('onboarding.ownerOnlyHint')}
        />
      </>
    );
  }

  if (onboarding.isPending) {
    return (
      <>
        <PageHeader title={t('onboarding.title')} description={t('onboarding.description')} />
        <ListSkeleton rows={5} />
      </>
    );
  }

  const progress = onboarding.data;
  if (!progress) {
    return (
      <>
        <PageHeader title={t('onboarding.title')} />
        <EmptyState
          icon={AlertTriangle}
          title={t('error.unexpected')}
          description={t('onboarding.loadFailed')}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('onboarding.title')} description={t('onboarding.description')} />

      <div className="flex flex-col gap-6">
        <GuidedTour progress={progress} />

        <Card>
          <CardHeader>
            <CardTitle>{t('onboarding.checklistTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <SetupChecklist progress={progress} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center gap-3">
            <Upload aria-hidden="true" className="size-5 text-muted-foreground" />
            <CardTitle>{t('import.title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">{t('import.pitch')}</p>
            <div>
              <Button asChild variant="secondary">
                <Link to="/customers/import">{t('import.start')}</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <SampleDataCard progress={progress} />
      </div>
    </>
  );
}

export default SetupPage;
